import React, { useEffect, useState } from 'react';
import { AlertCircle, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { useSelector } from 'react-redux';
import { DashboardApi } from '../../../apis/DashboardApi';
import { useNavigate } from 'react-router-dom';

interface SubscriptionStatusProps {
  className?: string;
}

const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({ className }) => {
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const userLoginData = useSelector((state: any) => state.data.login.value);
  const isLifetime = userLoginData?.data?.user_plan?.is_lifetime === true;

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const response:any = await DashboardApi.getUserSubscription();
      if (response?.data?.status === 'success') {
        setSubscription(response.data.data.subscription);
      }
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching subscription:", error);
      setError(error.message || "Failed to load subscription");
      setLoading(false);
    }
  };

  // If account is suspended, render a full-screen message
//   if (subscription?.status === 'suspended') {
//     return (
//       <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center p-6">
//         <AlertCircle className="text-red-500 w-16 h-16 mb-4" />
//         <h1 className="text-2xl font-bold text-red-500 mb-2">Account Suspended</h1>
//         <p className="text-center mb-6 max-w-md">
//           Your account has been suspended due to an expired subscription. Please renew your 
//           subscription to regain access to your account and its features.
//         </p>
//         <button 
//           onClick={() => navigate('/upgrade')}
//           className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium"
//         >
//           Renew Subscription
//         </button>
//       </div>
//     );
//   }

  // Lifetime users never see expiration/payment status cards
  if (isLifetime) {
    return null;
  }

  if (loading) {
    return <div className={`${className} p-4 animate-pulse bg-gray-100 rounded-lg`}></div>;
  }

  if (error) {
    return null; // Don't show error states in the UI
  }

  if (!subscription) {
    return null; // No subscription data yet
  }

  if (subscription.is_lifetime) {
    return null; // Lifetime users never see expiration/payment status cards
  }

  if (subscription.status === 'active' && subscription.auto_renew) {
    return null; // Active subscription with auto-renew - no need to show warnings
  }

  // For subscriptions that are active but not set to auto-renew or in grace period
  return (
    <div className={`${className} rounded-lg p-4 mb-4`}>
      {subscription.status === 'cancelled' ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-800">Subscription Cancelled</h3>
              <p className="text-sm text-red-700">
                Your subscription has been cancelled and will expire on{' '}
                {new Date(subscription.end_date).toLocaleDateString()}.
              </p>
              <button
                onClick={() => navigate('/upgrade')}
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-800"
              >
                Renew Subscription →
              </button>
            </div>
          </div>
        </div>
      ) : subscription.status === 'active' && subscription.expiration_notified_at ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-800">Subscription Expiring</h3>
              <p className="text-sm text-amber-700">
                Your subscription has expired but is in grace period until{' '}
                {subscription.suspended_at
                  ? new Date(subscription.suspended_at).toLocaleDateString()
                  : 'soon'}.
              </p>
              <button
                onClick={() => navigate('/upgrade')}
                className="mt-2 text-sm font-medium text-amber-700 hover:text-amber-800"
              >
                Renew Now →
              </button>
            </div>
          </div>
        </div>
      ) : subscription.status === 'active' && !subscription.auto_renew ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Clock className="text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800">Auto-Renewal Disabled</h3>
              <p className="text-sm text-blue-700">
                Your subscription will expire on{' '}
                {new Date(subscription.end_date).toLocaleDateString()}.
              </p>
              <button
                onClick={() => navigate('/user-settings')}
                className="mt-2 text-sm font-medium text-blue-700 hover:text-blue-800"
              >
                Enable Auto-Renewal →
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SubscriptionStatus;